import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { AnswerForType } from "@tellescope/types-models";
import { useCallback, useMemo } from "react";
import {
  isSubmittableResponse,
  normalizeResponseForSubmission,
  subFieldIdsOf,
  substituteResponseFieldText,
} from "../../utils";
import type { FormFieldNode } from "../../utils/types";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";

export const IMMEDIATE_SAVE_FIELD_TYPES = ["Stripe", "Appointment Booking", "Chargebee"] as const;

export type ImmediateSaveFieldType = (typeof IMMEDIATE_SAVE_FIELD_TYPES)[number];

export type ImmediateSaveAnswer = {
  [TField in ImmediateSaveFieldType]: { type: TField; value: AnswerForType[TField] };
}[ImmediateSaveFieldType];

export interface UseSaveFieldResponseArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
  responseId: string | undefined;
  responseAccessCode: string | undefined;
}

export const useSaveFieldResponse = ({ core, responseId, responseAccessCode }: UseSaveFieldResponseArgs) => {
  const preparedResponseId = responseId ?? core.prepareFormResponseMutation.data?.response.id;
  const accessCode = responseAccessCode ?? core.prepareFormResponseMutation.data?.response.accessCode;

  const canSave = !!preparedResponseId || !!accessCode;

  const target = useMemo(() => ({ formResponseId: preparedResponseId, accessCode }), [preparedResponseId, accessCode]);

  const substitutionContext = useMemo(
    () => ({
      enduser: core.enduser,
      responses: Object.values(core.currentResponses),
      fieldsMap: core.fields?.fieldsMap,
    }),
    [core.enduser, core.currentResponses, core.fields?.fieldsMap]
  );

  const saveActiveFieldAndSubFields = useCallback(
    (activeField: FormFieldNode) => {
      const currentResponse = core.currentResponses[activeField.value.id];
      if (!currentResponse || !canSave) return;

      if (isSubmittableResponse(currentResponse)) {
        core.saveFieldResponseMutation.mutate({
          ...target,
          response: substituteResponseFieldText(currentResponse, substitutionContext),
        });
      }

      subFieldIdsOf(activeField.value).forEach((subFieldId) => {
        const subResponse = core.currentResponses[subFieldId];
        if (!subResponse || !isSubmittableResponse(subResponse)) return;
        core.saveFieldResponseMutation.mutate({
          ...target,
          response: substituteResponseFieldText(subResponse, substitutionContext),
        });
      });
    },
    [core.currentResponses, core.saveFieldResponseMutation, canSave, target, substitutionContext]
  );

  const saveImmediateFieldResponse = useCallback(
    (field: TellescopeModelFromName<"form_fields">, answer: ImmediateSaveAnswer) => {
      if (!canSave) return;

      core.saveFieldResponseMutation.mutate({
        ...target,
        response: substituteResponseFieldText(
          {
            fieldId: field.id,
            fieldTitle: field.title ?? "",
            externalId: field.externalId,
            fieldDescription: field.description,
            fieldHtmlDescription: field.htmlDescription,
            answer,
          },
          substitutionContext
        ),
      });
    },
    [core.saveFieldResponseMutation, canSave, target, substitutionContext]
  );

  const saveFieldResponse = useCallback(
    async (activeField: FormFieldNode | undefined) => {
      if (!activeField) return;

      const currentResponse = core.currentResponses[activeField.value.id];
      if (!currentResponse || !canSave || !isSubmittableResponse(currentResponse)) return;

      await core.saveFieldResponseMutation.mutateAsync({
        ...target,
        response: normalizeResponseForSubmission(substituteResponseFieldText(currentResponse, substitutionContext)),
      });
    },
    [core.currentResponses, core.saveFieldResponseMutation, canSave, target, substitutionContext]
  );

  return { saveActiveFieldAndSubFields, saveImmediateFieldResponse, saveFieldResponse };
};
